import _ from 'lodash';
import classNames from 'classnames';
import React, { Component, PropTypes } from 'react';
import { addAlertToStore, findAlertInStore, removeAlertFromStore } from '../../utils/AlertUtils';
import { convertHoursToSeconds, getDateTimeInSeconds } from '../../utils/DateUtils';
import Timestamp from 'components/Timestamp';
import ChevronRight from 'components/icons/ChevronRight';
import CancelIcon from 'components/icons/Cancel';

const SUPPORTED_TYPES = ['story', 'clip', 'link'];

class NewsBanner extends Component {
  static propTypes = {
    FRN_componentInstanceId: PropTypes.string,
    FRN_rawResponses: PropTypes.array,
    title: PropTypes.string,
    titleColor: PropTypes.string,
    backgroundColor: PropTypes.string,
    expandBackgroundColor: PropTypes.string,
    textAlign: PropTypes.oneOf(['left', 'center', 'right']),
    textColor: PropTypes.string,
    showTimestamp: PropTypes.bool,
    startArticleIndex: PropTypes.number,
    numberOfItems: PropTypes.number,
    dismissOptions: PropTypes.shape({
      isDismissable: PropTypes.bool,
      dismissTimeInHours: PropTypes.number
    }),
    timestampOptions: PropTypes.shape({
      showElapsedTime: PropTypes.bool,
      displayShortDateTime: PropTypes.bool
    })
  }

  static defaultProps = {
    titleColor: '#000',
    backgroundColor: '#FFF',
    expandBackgroundColor: '#FFF',
    textAlign: 'left',
    textColor: '#000',
    showTimestamp: true,
    startArticleIndex: 0,
    numberOfItems: 0,
    dismissOptions: {
      isDismissable: false,
      dismissTimeInHours: 24
    }
  }

  state = {
    isExpanded: false,
    showAlert: true
  }

  componentWillMount() {
    const {FRN_componentInstanceId, dismissOptions: { isDismissable } } = this.props;

    if (isDismissable) {
      const alertInStore = findAlertInStore(FRN_componentInstanceId);

      if (alertInStore) {
        const { timeout, data } = alertInStore;

        const time = getDateTimeInSeconds();
        const showAlert = time > timeout || this._isNewData(data);

        if (showAlert) {
          removeAlertFromStore(FRN_componentInstanceId);
        } else {
          this._startAutoShowAlertInterval(timeout - time);
        }

        this.setState({ showAlert });
      }
    }
  }

  componentWillUnmount() {
    clearInterval(this._showAlertAgainInterval);
  }

  _dismissAlert() {
    const { FRN_componentInstanceId, dismissOptions: { dismissTimeInHours } } = this.props;
    const timeout = convertHoursToSeconds(dismissTimeInHours);

    addAlertToStore(FRN_componentInstanceId, getDateTimeInSeconds() + timeout, this.props.FRN_rawResponses);
    this._startAutoShowAlertInterval(timeout);

    this.setState({
      showAlert: false
    });
  }

  _startAutoShowAlertInterval(timeout) {
    this._showAlertAgainInterval = setInterval(() => {
      removeAlertFromStore(this.props.FRN_componentInstanceId);
      clearInterval(this._showAlertAgainInterval);

      this.setState({
        showAlert: true
      });
    }, timeout);
  }

  _isNewData(rawResponse) {
    const { startArticleIndex } = this.props;

    const {
      FRN_rawResponses: [{
        data: {
          features: newFeatures = []
        } = {}
      } = {}] = [],
    } = this.props;

    const [{
      data: {
        features: oldFeatures = []
      } = {}
    } = {}] = rawResponse;

    const { id: newId } = newFeatures[startArticleIndex] || {};
    const { id: oldId } = oldFeatures[startArticleIndex] || {};

    return oldId !== newId;
  }

  _toggleListItems() {
    this.setState({isExpanded: !this.state.isExpanded});
  }

  render() {
    const {
      showAlert,
      isExpanded
    } = this.state;

    if (!showAlert) {
      return null;
    }

    const {
      FRN_rawResponses: [{
        data: {
          features = []
        } = {}
      } = {}] = [],
      title,
      titleColor,
      backgroundColor,
      expandBackgroundColor,
      textAlign,
      textColor,
      showTimestamp,
      timestampOptions: {
        showElapsedTime,
        displayShortDateTime
      } = {},
      startArticleIndex,
      numberOfItems,
      dismissOptions: { isDismissable }
    } = this.props;

    const offset = Math.max(startArticleIndex, 0);
    const filteredItems = features.filter(item => _.includes(SUPPORTED_TYPES, _.toLower(item.type)));
    const items = filteredItems.slice(offset, numberOfItems + offset);

    if (!items.length) {
      return null;
    }

    const totalItemsToDisplay = numberOfItems > 0 ? Math.min(numberOfItems, items.length) : items.length;
    const totalItemsVisible = (!isExpanded && 1) || (numberOfItems > 0 ? numberOfItems : items.length);
    const newsItems = items.slice(0, totalItemsVisible);

    if (!newsItems.length) {
      return null;
    }

    const newsItemsToShow = isExpanded ? newsItems : newsItems.slice(0, 1);

    let additionalNewsItemRows;

    const newsItemRows = newsItemsToShow.map((item, index) => {
      const {
        headline = '',
        publishedDate: publishDate = '',
        updatedDate: updatedDate = '',
        link = '',
        type = '',
        id = '',
        url = '',
        seo: { pageurl: slug = ''} = {}
      } = item;

      const newsItemStyle = {
        backgroundColor: index > 0 ? expandBackgroundColor : backgroundColor,
        color: textColor,
        textAlign
      };

      const linkHref = (_.toLower(type) === 'link') ? url : null;
      const href = (typeof location !== 'undefined') ?
        location.protocol + '//' + location.hostname + (location.port ? ':' + location.port : '') + '/' + type + '/' + id + '/' + slug : link;

      const newsItemClasses = classNames('NewsBanner-item', {'NewsBanner-firstItem': index === 0});
      return (
        <a href={linkHref || href} className={newsItemClasses} style={newsItemStyle} key={index}>
          <div className={classNames('NewsBanner-text', { showTimestamp: 'NewsBanner-text--showTimestamp' }, totalItemsToDisplay > 1 ? 'NewsBanner-text--withToggleBtn' : null)}>
            <div className="NewsBanner-headline">
              {index === 0 ?
                <span className="NewsBanner-title" style={{color: titleColor}}>
                  {title}
                </span> : null }
              {headline}
            </div>
            {showTimestamp ?
              <div className="NewsBanner-time">
                <Timestamp updatedDate={updatedDate} publishDate={publishDate} showElapsedTime={showElapsedTime} displayShortDateTime={displayShortDateTime}/>
              </div> : null}
          </div>

          {index > 0 ?
            <div className="NewsBanner-iconAngleRight">
              <ChevronRight color={textColor}/>
            </div> : null}

        </a>
      );
    });

    const firstNewsItemRow = newsItemRows[0];

    if (isExpanded) {
      additionalNewsItemRows = (
        <div className="NewsBanner-expandedItems">
          {newsItemRows.slice(1, newsItemRows.length)}
        </div>
      );
    }

    const toggleBtnColor = {
      color: backgroundColor
    };

    return (
      <div className="NewsBanner">
        { isDismissable && <div className="NewsBanner-dismissBtn" onClick={() => this._dismissAlert()}><CancelIcon color={textColor}/></div> }
        {totalItemsToDisplay > 1 ?
          <div className="NewsBanner-toggleBtn" onClick={() => this._toggleListItems()}>
            <a>
              <i style={toggleBtnColor} className="fa fa-exclamation-triangle" aria-hidden="true"></i>
              <span style={toggleBtnColor}>{totalItemsToDisplay}</span>
            </a>
          </div> : null}
        <div className="NewsBanner-content">
          {firstNewsItemRow}
          {additionalNewsItemRows}
        </div>
      </div>
    );
  }
}

export default NewsBanner;



// WEBPACK FOOTER //
// ./app/components/news/NewsBanner.js